<template>
  <ModalDialog
    ref="dialog"
    title="Allocate Overpayment"
    :submitting="submitting"
    :submitDisabled="submitting || !canSubmitAllocations"
    submitTitle="Allocate Overpayment"
    icon="fa-sack-dollar"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm">
      <FormRow label="Overpayment">
        <LinkOverPayment :id="overPayment.id" :name="overPayment.sequenceNumber" />
      </FormRow>
      <div class="add-op-container">
        <div v-if="!loading && !loadingError && overPayment.id">
          <FormRow label="Available">
            <FormStaticLabel><strong>{{availableAmount | formatCurrency}}</strong></FormStaticLabel>
          </FormRow>
          <FormRow label="After Allocations">
            <FormStaticLabel><strong>{{currentAvailableAmount | formatCurrency}}</strong></FormStaticLabel>
          </FormRow>

          <FormHorizontalRule class="my-2" />
          <TabbedBox>
            <template v-slot:tablist>
              <Tab :first="true" tabid="tab-allocations-invoice">
                <i class="far fa-envelope-open-dollar"></i> Invoice Allocations
              </Tab>
              <Tab tabid="tab-allocations-orders">
                <i class="far fa-shopping-cart"></i> Order Allocations
              </Tab>
            </template>
            <template v-slot:tabcontent>
              <TabPage :first="true" tabid="tab-allocations-invoice">
                <InvoiceAllocation
                  :contactId="overPayment.contactId"
                  :isFixedAmount="true"
                  :isAllocationOnly="true"
                  :paymentAmount="currentAvailableAmount"
                  v-model="availableInvoiceList" />
              </TabPage>
              <TabPage tabid="tab-allocations-orders">
                <OrderAllocationList
                  :contactId="overPayment.contactId"
                  :paymentAmount="currentAvailableAmount"
                  :isFixedAmount="true"
                  v-model="availableOrderList"
                />
              </TabPage>
            </template>
          </TabbedBox>        
        </div>
      </div>
    </FormGroup>
  </ModalDialog>
</template>

<style scoped>
.add-op-container {
  min-height: 300px;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import Utils from '@/utils/utils'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import ContactSearchableSelect from '@/modules/contacts/components/contactSearchableSelect'
import InvoiceAllocation from '@/modules/accounts/components/invoiceAllocation'
import OrderAllocationList from '@/modules/accounts/components/orderAllocationList'

import { InvoiceStatusTypes } from '@/store/types'

export default {
  name: 'OverPaymentAllocationDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    ContactSearchableSelect,
    InvoiceAllocation,
    OrderAllocationList
  },
  props: {
    overPaymentId: Number
  },
  data() {
    return {
      dialogForm: {},
      overPayment: { },
      availableInvoiceList: [],
      availableOrderList: [],
      loading: false,
      loadingError: '',
      submitting: false
    }
  },
  created() {
  },
  mounted() {
  },
  computed: {
    canSubmitAllocations() {
      if(this.availableAmount < this.totalInvoiceAllocation + this.totalOrderAllocation)
        return false
      if(this.totalInvoiceAllocation <= 0 && this.totalOrderAllocation <= 0)
        return false
      if(this.totalInvoiceErrorCount > 0 || this.totalOrderErrorCount > 0)
        return false
      if(this.availableAmount == 0)
        return false

      return true
    },
    availableAmount() {
      return this.parseDecimal(this.overPayment.remainingCredit) - this.parseDecimal(this.overPayment.totalReservedAmount)
    },
    currentAvailableAmount() {
      let availableAmount = Utils.roundCurrency(this.availableAmount - this.totalInvoiceAllocation - this.totalOrderAllocation)
      return Math.max(availableAmount, 0)
    },
    totalAllocationValue() {
      return Utils.roundCurrency(this.totalInvoiceAllocation + this.totalOrderAllocation)
    },
    totalInvoiceAllocation() {
      return this.availableInvoiceList.reduce((curVal, invoice) => {
        return Utils.roundCurrency(curVal + this.parseDecimal(invoice.allocatedAmount))
      }, 0)
    },
    totalInvoiceErrorCount() {
      return this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > invoice.amountDue).length
    },
    totalOrderErrorCount() {
      return this.availableOrderList.filter(order => this.parseDecimal(order.allocatedAmount) > order.amountDue).length
    },
    totalOrderAllocation() {
      return this.availableOrderList.reduce((curVal, order) => {
        return Utils.roundCurrency(curVal + this.parseDecimal(order.allocatedAmount))
      }, 0)
    }
  },
  methods: {
    show(options = {}) {
      // Reset form
      this.responseInfo = { }
      this.dialogForm = { }

      this.requestData()

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      let invoiceCount = this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > 0).length
      let orderCount = this.availableOrderList.filter(order => this.parseDecimal(order.allocatedAmount) > 0).length
      let paymentDisplay = Utils.formatCurrency(this.totalAllocationValue)
      
      var message = `Confirm allocation of <strong>${paymentDisplay}</strong> applied to <strong>${invoiceCount}</strong> invoice(s) and <strong>${orderCount}</strong> order(s)?`
      this.confirmMessage('Confirm Overpayment Allocations?', null, { html: message })
      .then(success => {
        if(success) {
          this._submitOverpaymentAllocations()
        }
      })
    },
    _submitOverpaymentAllocations() {
      let invoiceAllocationList = this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > 0).map(invoice => {
        return {
          invoiceId: invoice.id,
          amount: Utils.roundCurrency(this.parseDecimal(invoice.allocatedAmount))
        }
      })
      let orderAllocationList = this.availableOrderList.filter(order => this.parseDecimal(order.allocatedAmount) > 0).map(order => {
        return {
          orderId: order.id,
          amount: Utils.roundCurrency(this.parseDecimal(order.allocatedAmount))
        }
      })

      var overpaymentAllocData = {
        invoiceAllocationList: invoiceAllocationList,
        orderAllocationList: orderAllocationList
      }

      this.submitting = true
      this.$apiService.accountsService.addOverpaymentAllocations(this.overPaymentId, overpaymentAllocData)
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.submitting = false
      })
    },

    requestData() {
      this.overPayment = {}
      this.loading = true
      this.loadingError = ''

      this.$apiService.accountsService.getOverPayment(this.overPaymentId)
      .then(response => {
        this.overPayment = response.data
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    }
  }
}
</script>